import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { api, API_ENDPOINTS } from '../lib/api';
import { AuthResponse } from '../types/auth';

/**
 * Social Authentication Service
 * Handles Google and Facebook sign-in via @capacitor-firebase/authentication.
 * Works on both Android (native Google/Facebook SDK) and web (Firebase popup).
 *
 * IMPORTANT: getIdToken() must be called AFTER signInWithGoogle/signInWithFacebook —
 * result.credential?.idToken from signInWithGoogle() is the OAuth provider token,
 * NOT the Firebase ID token. Only FirebaseAuthentication.getIdToken() returns
 * the Firebase JWT that the backend firebase-admin SDK can verify.
 */
export const socialAuthService = {
  signInWithGoogle: async (): Promise<AuthResponse> => {
    // Step 1: Native Android Google Sign-In dialog (or web popup on browser)
    await FirebaseAuthentication.signInWithGoogle();
    // Step 2: Get Firebase ID token — NOT result.credential?.idToken (that's OAuth token)
    const { token: idToken } = await FirebaseAuthentication.getIdToken();
    // DIAGNOSTIC — remove after root cause confirmed
    console.log('[SocialAuth] idToken length:', idToken?.length, 'looks like JWT:', (idToken?.split('.').length === 3), 'first30:', idToken?.substring(0, 30));
    // Step 3: Exchange Firebase ID token for app JWT
    const response = await api.post<AuthResponse>(
      API_ENDPOINTS.AUTH.SOCIAL_LOGIN,
      { idToken, provider: 'google' }
    );
    // DIAGNOSTIC — remove after root cause confirmed
    console.log('[SocialAuth] response status:', response.status, 'data keys:', Object.keys(response.data || {}));
    return response.data;
  },

  signInWithFacebook: async (): Promise<AuthResponse> => {
    // Step 1: Native Android Facebook Login dialog (or web popup on browser)
    await FirebaseAuthentication.signInWithFacebook();
    // Step 2: Get Firebase ID token
    const { token: idToken } = await FirebaseAuthentication.getIdToken();
    // Step 3: Exchange Firebase ID token for app JWT
    const response = await api.post<AuthResponse>(
      API_ENDPOINTS.AUTH.SOCIAL_LOGIN,
      { idToken, provider: 'facebook' }
    );
    return response.data;
  },

  signInWithApple: async (): Promise<AuthResponse> => {
    // Step 1: Native Apple Sign-In dialog (or web popup on browser/Android)
    await FirebaseAuthentication.signInWithApple();
    // Step 2: Get Firebase ID token — NOT the Apple identity token
    const { token: idToken } = await FirebaseAuthentication.getIdToken();
    // Step 3: Exchange Firebase ID token for app JWT
    const response = await api.post<AuthResponse>(
      API_ENDPOINTS.AUTH.SOCIAL_LOGIN,
      { idToken, provider: 'apple' }
    );
    return response.data;
  },
};
